import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormValidationWithState } from '@makemydeal/dr-shared-ui-utils';
import { getMidDayUTCTime } from '@makemydeal/dr-dash-components';
import {
    tradeInValidationConfig,
    outsideLienholderValidationConfig
} from '@makemydeal/dr-shared-ui-utils/validationConfigs/tradeInValidationConfig';
import { useContractFlowAdapter } from '@makemydeal/dr-dash-components';
import type { ContractFlowValidationEngine } from '@makemydeal/dr-dash-components';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';

import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';

import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { updateTradeInErrorState } from '../lienholderForm/utils';
import type { LienholderFormData } from '../../../types';

type SingleTradeInContext = {
    tradeInIndex: number;
    tradeInValidator: ContractFlowValidationEngine;
    tradeInFormErrors: any;
    tradeInFormValues: any;
    hasContractErrors: boolean;
    softSaveLienHolderData: (lienholderData: LienholderFormData) => void;
};

export type SingleTradeInFormContextType = SingleTradeInContext & {
    lienholderValidator: ContractFlowValidationEngine;
    lienholderFormErrors: any;
};

type SingleTradeInFormProviderProps = {
    children: ReactNode;
    tradeInIndex?: number;
    isOutsideLienholder?: boolean;
    onContractingErrorsAlert?: (hasErrors: boolean) => void;
};

const SingleTradeInFormProviderContext = createContext<SingleTradeInContext | undefined>(undefined);

export const SingleTradeInFormProvider = ({
    children,
    tradeInIndex = 0,
    isOutsideLienholder = false,
    onContractingErrorsAlert
}: SingleTradeInFormProviderProps) => {
    const dispatch = useDispatch();
    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const amountOwed = useSelector(tradeInsSelectors.getTradeInAmountOwed(tradeInIndex)) || 0;
    const vin = useSelector(tradeInsSelectors.getVin(tradeInIndex)) || '';
    const isOptionalDesking = useSelector(dealModeSelectors.isOptionalDeskingMode);
    const isOptionalDeskingActive = isOptionalDesking && !isContractingContext;

    const onCommit = useCallback(
        (errorFlags: Record<string, boolean>) => {
            if (isOutsideLienholder) {
                dispatch(dealScreenErrorActionCreators.setErrorFields(DealTabs.SUMMARY, errorFlags));
            } else {
                dispatch((innerDispatch: any, getState: any) => {
                    const latestTradeInErrors = dealScreenErrorSelectors.getCategoryValidationErrors(
                        getState(),
                        DealTabs.TRADE_IN,
                        DealTabs.TRADE_IN
                    );
                    innerDispatch(
                        dealScreenErrorActionCreators.setErrorFieldsByCategory(
                            DealTabs.TRADE_IN,
                            DealTabs.TRADE_IN,
                            updateTradeInErrorState(latestTradeInErrors, tradeInIndex, errorFlags)
                        )
                    );
                });
            }
        },
        [dispatch, isOutsideLienholder, tradeInIndex]
    );

    const {
        engine: tradeInBaseValidator,
        formErrors: tradeInFormErrors,
        formValues
    } = useFormValidationWithState(
        {
            initialConfig: isOutsideLienholder ? outsideLienholderValidationConfig : tradeInValidationConfig,
            initialValues: {
                vehicle: { vin },
                lienholder: { amountOwed }
            },
            dispatchMode: 'onCommit',
            commitConfig: {
                keyStrategy: 'path',
                onCommit
            }
        },
        { channel: 'inline' }
    );

    const { engine: tradeInValidator, hasContractErrors } = useContractFlowAdapter(tradeInBaseValidator, tradeInFormErrors);

    useEffect(() => {
        onContractingErrorsAlert?.(hasContractErrors);
    }, [hasContractErrors]);

    useEffect(() => {
        // When contracting context is active, bypass OptionalDesking so Required errors can be highlighted
        tradeInValidator.ctx.dealMode = isOptionalDeskingActive ? 'OptionalDesking' : undefined;
    }, [tradeInValidator, isOptionalDesking, isContractingContext]);

    const softSaveLienHolderData = useCallback(
        (lienholderData: LienholderFormData) => {
            const goodThroughDate = lienholderData.payoffDueDate ? getMidDayUTCTime(lienholderData.payoffDueDate) : undefined;

            const dispatchData = {
                index: tradeInIndex,
                lienHolder: lienholderData.lienholder,
                lienHolderAccountNumber: lienholderData.lienholderAccountNumber,
                lienHolderPhoneNumber: lienholderData.lienholderPhoneNumber,
                lienHolderAddress: lienholderData.lienholderAddress,
                goodThroughDate,
                perDiemInterest: lienholderData.perDiem
            };

            dispatch(tradeInsActionCreators.updateTradeInLienholderFull({ ...dispatchData, index: tradeInIndex }));

            tradeInValidator.commit();

            if (isContractingContext) {
                dispatch(contractingActionCreators.refreshContractValidationErrors());
            }
        },
        [dispatch, tradeInIndex, tradeInValidator, isContractingContext]
    );

    useEffect(() => {
        tradeInValidator.setValues({ lienholder: { amountOwed } }, { mode: 'merge', validate: true });
        tradeInValidator.commit(['lienholder.name'], { validate: true });
    }, [amountOwed, tradeInValidator]);

    useEffect(() => {
        tradeInValidator.setValues({ vehicle: { vin } }, { mode: 'merge', validate: true });
    }, [vin, tradeInValidator]);

    useEffect(() => {
        tradeInValidator.validateAll();
    }, [isContractingContext, tradeInValidator]);

    return (
        <SingleTradeInFormProviderContext.Provider
            value={{
                tradeInIndex,
                tradeInValidator,
                tradeInFormErrors,
                tradeInFormValues: formValues,
                hasContractErrors: isOutsideLienholder ? false : hasContractErrors,
                softSaveLienHolderData
            }}
        >
            {children}
        </SingleTradeInFormProviderContext.Provider>
    );
};

export const useSingleTradeInFormProvider = (): SingleTradeInFormContextType => {
    const context = useContext(SingleTradeInFormProviderContext);

    const defaultTradeInValidator = {
        setValue: () => undefined,
        setValues: () => undefined,
        validatePath: () => undefined,
        validateAll: () => undefined,
        commit: () => undefined,
        getHasFormErrors: () => false,
        getHasContractErrors: () => false
    } as unknown as ContractFlowValidationEngine;

    const defaultContext: SingleTradeInContext = {
        tradeInIndex: 0,
        tradeInValidator: defaultTradeInValidator,
        tradeInFormErrors: {
            lienholder: {
                name: { hasError: false, errorMessage: '' },
                accountNumber: { hasError: false, errorMessage: '' },
                streetAddress: { hasError: false, errorMessage: '' },
                city: { hasError: false, errorMessage: '' },
                state: { hasError: false, errorMessage: '' },
                zip: { hasError: false, errorMessage: '' }
            }
        },
        tradeInFormValues: {},
        hasContractErrors: false,
        softSaveLienHolderData: () => undefined
    };

    const resolvedContext = context ?? defaultContext;

    return {
        ...resolvedContext,
        lienholderValidator: resolvedContext.tradeInValidator,
        lienholderFormErrors: resolvedContext.tradeInFormErrors
    };
};
